import type { CollectionSummaryJSONObject, FastExportType, IkeActionFastExport } from '@ikegps/core-api';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { T } from '../../i18n';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { AppStore } from '../../store';
import { getLongRunningTaskManager } from '../long.running.task.manager';
import { PDFReportMaker, ReportTypeEnum } from '../report/ReportMaker';

export class ExportMenuControllerImpl {
	hasOCalcForm(collectionSummaries: CollectionSummaryJSONObject[]): boolean {
		for (const summary of collectionSummaries) {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			if (formSummary != null && formSummary.metadata != null && formSummary.metadata.ocalc != null) {
				return true;
			}
		}

		return false;
	}

	hasSPIDACalcForm(collectionSummaries: CollectionSummaryJSONObject[]): boolean {
		for (const summary of collectionSummaries) {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			if (formSummary != null && formSummary.metadata != null && formSummary.metadata.spidacalc != null) {
				return true;
			}
		}

		return false;
	}

	hasPoleforeman(collectionSummaries: CollectionSummaryJSONObject[]): boolean {
		for (const summary of collectionSummaries) {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			if (formSummary?.metadata?.poleforeman) {
				return true;
			}
		}

		return false;
	}

	hasIkePoleForeman(collectionSummaries: CollectionSummaryJSONObject[]): boolean {
		for (const summary of collectionSummaries) {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			if (formSummary?.metadata?.ikePoleForeman) {
				return true;
			}
		}

		return false;
	}

	hasDDS(collectionSummaries: CollectionSummaryJSONObject[]): boolean {
		for (const summary of collectionSummaries) {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			if (formSummary?.metadata?.dds) {
				return true;
			}
		}

		return false;
	}

	// ── Fast exports (synchronous — binary stream response) ──

	async exportKML(options?: { silent?: boolean }): Promise<string> {
		return this.executeFastExport('KML', options);
	}

	async exportJSON(options?: { silent?: boolean }): Promise<string> {
		return this.executeFastExport('JSON', options);
	}

	// ── Long-running exports (async — task system + download manager) ──

	async exportExcel(options?: { silent?: boolean }): Promise<string> {
		const revisions = this.getRevisions();
		return getLongRunningTaskManager().service.startExportTask('EXCEL', revisions, 'Excel', this.getExportLabel(), {
			silent: options?.silent,
		});
	}

	async exportImportableExcel(options?: { silent?: boolean }): Promise<string> {
		const revisions = this.getRevisions();
		return getLongRunningTaskManager().service.startExportTask(
			'IMPORTABLE_EXCEL',
			revisions,
			'Importable Excel',
			this.getExportLabel(),
			{ silent: options?.silent }
		);
	}

	async exportOCalc(options?: { silent?: boolean }): Promise<string> {
		const revisions = this.getRevisions((summary) => {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			return formSummary != null && formSummary.metadata != null && formSummary.metadata.ocalc != null;
		});
		return getLongRunningTaskManager().service.startExportTask('OCALC', revisions, 'OCalc', this.getExportLabel(), {
			silent: options?.silent,
		});
	}

	async exportSPIDACalc(noPhotos: boolean, options?: { silent?: boolean }): Promise<string> {
		const revisions = this.getRevisions((summary) => {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			return formSummary != null && formSummary.metadata != null && formSummary.metadata.spidacalc != null;
		});
		return getLongRunningTaskManager().service.startExportTask(
			'SPIDACALC',
			revisions,
			'SPIDACalc',
			this.getExportLabel(),
			{
				noPhotos,
				silent: options?.silent,
			}
		);
	}

	async exportPoleforeman(options?: { silent?: boolean }): Promise<string> {
		const revisions = this.getRevisions((summary) => {
			const formSummary = AppStore.FormSummary.get(summary.formRevision);
			return formSummary != null && formSummary.metadata != null && formSummary.metadata.poleforeman != null;
		});
		return getLongRunningTaskManager().service.startExportTask(
			'POLEFOREMAN',
			revisions,
			'Poleforeman',
			this.getExportLabel()
		);
	}

	async exportIkePoleForeman(options?: { silent?: boolean }): Promise<string> {
		const collectionSummaries: CollectionSummaryJSONObject[] = [];
		for (const summary of CurrentState.Tag.collections) {
			const formSummary = await AppStore.FormSummary.getAsync(summary.formRevision);
			if (formSummary?.metadata?.ikePoleForeman) {
				collectionSummaries.push(summary);
			}
		}
		const revisions = collectionSummaries.map((s) => s.revision);
		return getLongRunningTaskManager().service.startExportTask(
			'IKE_POLEFOREMAN',
			revisions,
			'IPF',
			this.getExportLabel(),
			{
				silent: options?.silent,
			}
		);
	}

	async exportDDS(options?: { silent?: boolean }): Promise<string> {
		const revisions = this.getRevisions();
		return getLongRunningTaskManager().service.startExportTask('DDS', revisions, 'DDS', this.getExportLabel(), {
			silent: options?.silent,
		});
	}

	async exportImageZip(options?: { silent?: boolean }): Promise<string> {
		const revisions = this.getRevisions();
		return getLongRunningTaskManager().service.startExportTask(
			'IMAGE_ZIP',
			revisions,
			'Images',
			this.getExportLabel(),
			{
				silent: options?.silent,
			}
		);
	}

	// ── PDF reports (client-side — unchanged) ──

	/**
	 * Creates a PDF report of collections and downloads it
	 * @param type The type of report to export
	 */
	async exportReport(type: ReportTypeEnum) {
		const revisions = CurrentState.Tag.collections.map((summary) => summary.revision);
		const collectionModels = await Promise.all(revisions.map((revision) => AppStore.Collection.fetch(revision)));

		return PDFReportMaker.makeReport(type, collectionModels);
	}

	// ── Export orchestration ──

	async runExport(func: () => Promise<string | void>): Promise<string | void> {
		try {
			return await func();
		} catch (_) {
			NotificationService.error({ key: T.ERROR_GENERAL });
		}
	}

	// ── Private helpers ──

	/**
	 * Execute a fast export (KML, JSON) via the `export.fast` action.
	 * Registers the export in the download manager so it appears as a row.
	 * Uses `actionRaw()` because the server streams binary data on success
	 * (not JSON), which the standard `action()` / `_post()` cannot handle.
	 */
	private async executeFastExport(type: FastExportType, options?: { silent?: boolean }): Promise<string> {
		const revisions = this.getRevisions();
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const jobName = this.getExportLabel();

		const taskManager = getLongRunningTaskManager();
		const taskId = taskManager.service.addFastExportTask(type, jobName, { silent: options?.silent });

		try {
			const response = await Services.Department.rest.actionRaw<IkeActionFastExport>('export.fast', {
				type,
				revisions,
				timezone,
			});

			if (!response.ok) {
				taskManager.service.failFastExportTask(taskId, undefined, 'Export failed');
				return taskId;
			}

			const disposition = response.headers.get('Content-Disposition') || '';

			if (!disposition.includes('attachment')) {
				// Error/warning path: server returned notifications instead of file data
				const json = await response.json();
				if (json.notifications != null && json.notifications.length > 0) {
					taskManager.service.failFastExportTask(taskId, json.notifications);
				} else {
					taskManager.service.failFastExportTask(taskId, undefined, 'Export failed');
				}
				return taskId;
			}

			// Success path: file response — download as blob
			const blob = await response.blob();
			const fileNameMatch = disposition.match(/filename="([^"]+)"/);
			const fileName = fileNameMatch ? fileNameMatch[1]! : `export.${type.toLowerCase()}`;
			const url = URL.createObjectURL(blob);
			taskManager.service.completeFastExportTask(taskId, url, fileName);
		} catch (_err) {
			taskManager.service.failFastExportTask(taskId, undefined, 'Export failed');
		}
		return taskId;
	}

	private getRevisions(filter?: (summary: CollectionSummaryJSONObject) => boolean): string[] {
		let collections = CurrentState.Tag.collections;
		if (filter != null) {
			collections = collections.filter(filter);
		}
		return collections.map((summary) => summary.revision);
	}

	getExportLabel(): string {
		const collections = CurrentState.Tag.collections;
		const tagIds = CurrentState.Tag.tags;

		if (collections.length === 1) {
			// Single pole: "Job title - Collection title"
			const collection = collections[0]!;
			const tagId = collection.tags[0];
			const jobName = tagId ? AppStore.Tag.get(tagId)?.name : null;
			const title = collection.title || 'Export';
			return jobName ? `${jobName} - ${title}` : title;
		}

		if (tagIds.length === 1) {
			// Multiple collections within same job: "Job title - X collections"
			const jobName = AppStore.Tag.get(tagIds[0]!)?.name;
			return jobName ? `${jobName} - ${collections.length} collections` : `${collections.length} collections`;
		}

		// Multiple collections from multiple jobs: "X jobs - Y collections"
		return `${tagIds.length} jobs - ${collections.length} collections`;
	}
}

export const ExportMenuController = new ExportMenuControllerImpl();
